// Utility to extract user information from chat messages

export interface ExtractedData {
  user_name?: string;
  user_email?: string;
  user_phone?: string;
  wedding_date?: string;
  venue?: string;
  services?: string[];
  user_type?: "prospect" | "client";
}

// Common name patterns: "I'm John", "My name is Sarah", "This is Mike", "I am Jessica"
const NAME_PATTERNS = [
  /(?:i'?m|my name is|this is|i am|call me)\s+([A-Z][a-z]+(?:\s+[A-Z][a-z]+)?)/i,
  /^([A-Z][a-z]+)(?:\s+here|!|\.|,)/i,
  /(?:^|\.\s+)([A-Z][a-z]+)\s+(?:and\s+)?(?:my\s+)?(?:partner|fianc[eé]+)/i,
];

// Email pattern
const EMAIL_PATTERN = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/;

// Phone patterns (various US formats)
const PHONE_PATTERNS = [
  /\b\d{3}[-.\s]?\d{3}[-.\s]?\d{4}\b/,
  /\(\d{3}\)\s*\d{3}[-.\s]?\d{4}/,
  /\b\d{10}\b/,
];

// Date patterns
const DATE_PATTERNS = [
  // Month name formats: "June 15, 2025", "June 15th 2025", "15 June 2025"
  /\b(January|February|March|April|May|June|July|August|September|October|November|December)\s+(\d{1,2})(?:st|nd|rd|th)?,?\s*(\d{4})\b/i,
  /\b(\d{1,2})(?:st|nd|rd|th)?\s+(January|February|March|April|May|June|July|August|September|October|November|December),?\s*(\d{4})\b/i,
  // Numeric formats: "6/15/2025", "06-15-2025", "2025-06-15"
  /\b(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})\b/,
  /\b(\d{4})[\/\-](\d{1,2})[\/\-](\d{1,2})\b/,
];

// Venue keywords
const VENUE_PATTERNS = [
  /(?:at|venue is|getting married at|ceremony at|reception at)\s+(?:the\s+)?([A-Z][A-Za-z\s&']+(?:hotel|estate|manor|barn|winery|vineyard|club|center|garden|hall|farm|inn|resort|venue))/i,
  /(?:our venue is|we booked|we're having it at)\s+(?:the\s+)?([A-Z][A-Za-z\s&']+)/i,
];

// Service interest patterns
const SERVICE_KEYWORDS = {
  photography: /\b(?:photo(?:graphy|s)?|photographer|pictures?)\b/i,
  videography: /\b(?:video(?:graphy)?|videographer|cinematography|film)\b/i,
  photobooth: /\b(?:photo\s*booth|booth)\b/i,
};

// User type detection
const EXISTING_CLIENT_PATTERNS = [
  /\b(?:we (?:already )?booked|our wedding was|we hired you|existing client|already a client)\b/i,
  /\b(?:checking (?:in|on)|status of|our photos|our video)\b/i,
];

const PROSPECT_PATTERNS = [
  /\b(?:we(?:'re| are) (?:getting married|planning|looking)|upcoming wedding|our wedding is)\b/i,
  /\b(?:interested in|looking for|need a|want to book|available|availability)\b/i,
];

const MONTH_MAP: Record<string, string> = {
  january: "01", february: "02", march: "03", april: "04",
  may: "05", june: "06", july: "07", august: "08",
  september: "09", october: "10", november: "11", december: "12",
};

function parseDate(match: RegExpMatchArray): string | null {
  try {
    // Check which pattern matched based on structure
    const fullMatch = match[0];
    
    // Month name first: "June 15, 2025"
    if (/^[A-Za-z]/.test(fullMatch)) {
      const month = MONTH_MAP[match[1].toLowerCase()];
      const day = match[2].padStart(2, "0");
      const year = match[3];
      return `${year}-${month}-${day}`;
    }
    
    // Day first with month name: "15 June 2025"
    if (/^\d{1,2}\s+[A-Za-z]/.test(fullMatch)) {
      const day = match[1].padStart(2, "0");
      const month = MONTH_MAP[match[2].toLowerCase()];
      const year = match[3];
      return `${year}-${month}-${day}`;
    }
    
    // YYYY-MM-DD format
    if (/^\d{4}[-\/]/.test(fullMatch)) {
      const year = match[1];
      const month = match[2].padStart(2, "0");
      const day = match[3].padStart(2, "0");
      return `${year}-${month}-${day}`;
    }
    
    // MM/DD/YYYY or MM-DD-YYYY format
    if (/^\d{1,2}[-\/]\d{1,2}[-\/]\d{4}$/.test(fullMatch)) {
      const month = match[1].padStart(2, "0");
      const day = match[2].padStart(2, "0");
      const year = match[3];
      return `${year}-${month}-${day}`;
    }
    
    return null;
  } catch {
    return null;
  }
}

export function extractDataFromMessage(
  content: string,
  role: "user" | "assistant"
): ExtractedData {
  const extracted: ExtractedData = {};
  
  // Only extract personal info from user messages
  if (role === "user") {
    // Extract name
    for (const pattern of NAME_PATTERNS) {
      const match = content.match(pattern);
      if (match && match[1]) {
        // Clean up the name
        const name = match[1].trim();
        // Avoid false positives like "I'm interested" or common words
        const skipWords = ["interested", "looking", "wondering", "hoping", "planning", "excited", "getting"];
        if (!skipWords.includes(name.toLowerCase()) && name.length > 1) {
          extracted.user_name = name;
          break;
        }
      }
    }
    
    // Extract email
    const emailMatch = content.match(EMAIL_PATTERN);
    if (emailMatch) {
      extracted.user_email = emailMatch[0].toLowerCase();
    }
    
    // Extract phone
    for (const pattern of PHONE_PATTERNS) {
      const match = content.match(pattern);
      if (match) {
        // Clean and format phone
        const phone = match[0].replace(/\D/g, "");
        if (phone.length === 10) {
          extracted.user_phone = phone;
          break;
        }
      }
    }
    
    // Extract date
    for (const pattern of DATE_PATTERNS) {
      const match = content.match(pattern);
      if (match) {
        const parsed = parseDate(match);
        if (parsed) {
          // Only use future dates for wedding dates
          const dateObj = new Date(parsed);
          if (dateObj > new Date()) {
            extracted.wedding_date = parsed;
            break;
          }
        }
      }
    }
    
    // Extract venue
    for (const pattern of VENUE_PATTERNS) {
      const match = content.match(pattern);
      if (match && match[1]) {
        extracted.venue = match[1].trim();
        break;
      }
    }
    
    // Extract service interests
    const services: string[] = [];
    for (const [service, pattern] of Object.entries(SERVICE_KEYWORDS)) {
      if (pattern.test(content)) {
        services.push(service);
      }
    }
    if (services.length > 0) {
      extracted.services = services;
    }
    
    // Detect user type
    for (const pattern of EXISTING_CLIENT_PATTERNS) {
      if (pattern.test(content)) {
        extracted.user_type = "client";
        break;
      }
    }
    if (!extracted.user_type) {
      for (const pattern of PROSPECT_PATTERNS) {
        if (pattern.test(content)) {
          extracted.user_type = "prospect";
          break;
        }
      }
    }
  }
  
  return extracted;
}

export function extractDataFromConversation(
  messages: Array<{ role: "user" | "assistant"; content: string }>
): ExtractedData {
  const combined: ExtractedData = {};
  
  for (const message of messages) {
    const extracted = extractDataFromMessage(message.content, message.role);
    
    // Merge extracted data, newer data overwrites older
    if (extracted.user_name) combined.user_name = extracted.user_name;
    if (extracted.user_email) combined.user_email = extracted.user_email;
    if (extracted.user_phone) combined.user_phone = extracted.user_phone;
    if (extracted.wedding_date) combined.wedding_date = extracted.wedding_date;
    if (extracted.venue) combined.venue = extracted.venue;
    if (extracted.user_type) combined.user_type = extracted.user_type;
    
    // Merge services arrays
    if (extracted.services) {
      const existing = combined.services || [];
      combined.services = [...new Set([...existing, ...extracted.services])];
    }
  }
  
  return combined;
}
